<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { Porthole } from '@client/components/porthole';
  import { SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  import { UsersService } from '@client/services/users';

  import { appStore } from '@client/stores/app';
  import { userStore } from '@client/stores/user';

  import { userMetadata } from '@client/utils/users';

  import { USER_REQUEST_PARAMS } from '@client/enums';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let users = [];
  let selectedUsers = [];
  let loading = false;
  let filterText = '';

  // Determine action type from step.action
  $: actionValue = step.action?.value || step.action;
  $: isEnableAction = actionValue === 'user_enable';
  $: isDisableAction = actionValue === 'user_disable';
  $: isReminderAction = actionValue === 'user_reminder';

  $: nextDisabled = !selectedUsers || selectedUsers.length === 0 || loading;

  const processUsers = (data) => {
    return data.map((user) => {
      const username = user.email.split('@').shift();
      const meta = userMetadata({ ...user, features: $userStore.features });

      return {
        ...user,
        ...meta,
        username,
        label: meta.name,
        value: user.email,
        removed: user.removed // Ensure removed field is preserved
      };
    });
  };

  const getUsers = async () => {
    loading = true;
    try {
      let hideRemoved;
      if (isEnableAction) {
        hideRemoved = false;
      } else if (isDisableAction) {
        hideRemoved = true;
      } else {
        hideRemoved = null;
      }

      const response = await UsersService.getUsers({
        ...USER_REQUEST_PARAMS,
        filterByDate: false,
        perPage: 500,
        hideRemoved
      });

      users = processUsers(response?.data || []).sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      notify($format('label.FAILURE.LOAD_USERS'), 'error');
    } finally {
      loading = false;
    }
  };

  const executeAction = async () => {
    if (!selectedUsers || selectedUsers.length === 0) {
      return { success: false };
    }

    $appStore.loading = true;
    const results = [];
    const errors = [];

    try {
      if (isReminderAction) {
        // Send reminder to users one at a time
        for (const user of selectedUsers) {
          try {
            const response = await UsersService.sendReminder({
              emails: [user.value]
            });

            if (response.status === 400) {
              errors.push({
                email: user.value,
                name: user.label,
                error: response.message
              });
            } else {
              results.push({
                email: user.value,
                name: user.label,
                success: true
              });
            }
          } catch (error) {
            errors.push({
              email: user.value,
              name: user.label,
              error: error.message
            });
          }
        }

        if (errors.length > 0) {
          notify(
            $format('label.FAILURE.SEND_REMINDER_COUNT', {
              values: { count: errors.length }
            }),
            'error'
          );
        }

        if (results.length > 0) {
          const userNames = results.map((r) => r.name).join(', ');
          notify($format('label.SUCCESS.SEND_REMINDER', { values: { user: userNames } }));
        }

        step.data = {
          ids: results.map((r) => r.email),
          rollback: {
            action: actionValue,
            results,
            errors,
            successCount: results.length,
            errorCount: errors.length
          }
        };

        return { success: errors.length === 0 };
      } else {
        // Enable or disable users one by one
        for (const user of selectedUsers) {
          try {
            // removed: true means disabled, removed: false means enabled
            const removed = isDisableAction ? true : false;
            const response = await UsersService.updateUser({
              email: user.value,
              removed,
              updateType: 'removed'
            });

            if (response.status === 400) {
              errors.push({
                email: user.value,
                name: user.label,
                error: response.message
              });
            } else {
              results.push({
                email: user.value,
                name: user.label,
                success: true,
                previousState: user.removed
              });
            }
          } catch (error) {
            errors.push({
              email: user.value,
              name: user.label,
              error: error.message
            });
          }
        }

        if (errors.length > 0) {
          notify(
            $format('label.FAILURE.UPDATE_USERS', {
              values: { count: errors.length }
            }),
            'error'
          );
        }

        if (results.length > 0) {
          const userNames = results.map((r) => r.name).join(', ');
          if (isEnableAction) {
            notify($format('label.SUCCESS.TOGGLE_USER_STATE_ENABLED', { values: { user: userNames } }));
          } else {
            notify($format('label.SUCCESS.TOGGLE_USER_STATE_DISABLED', { values: { user: userNames } }));
          }
        }

        // For enable/disable: store array of email IDs and rollback data
        step.data = {
          ids: results.map((r) => r.email),
          rollbackData: {
            action: actionValue,
            results,
            errors,
            successCount: results.length,
            errorCount: errors.length
          }
        };

        return { success: errors.length === 0 };
      }
    } catch (error) {
      notify($format('label.FAILURE.UPDATE_USERS'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const rollbackAction = async () => {
    if (!step.data?.rollbackData?.results) return;

    $appStore.loading = true;

    try {
      for (const result of step.data.rollbackData.results) {
        if (result.success && result.previousState !== undefined) {
          await UsersService.updateUser({
            email: result.email,
            removed: result.previousState,
            updateType: 'removed'
          });
        }
      }

      notify($format('label.SUCCESS.ROLLBACK'));
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  // Reload users when action type changes
  $: if (actionValue) {
    getUsers();
  }

  onMount(() => {
    if (onExecute) {
      onExecute(executeAction);
    }

    if (onRollback) {
      onRollback(rollbackAction);
    }
  });
</script>

<div class="user-actions-form">
  {#if loading}
    <div class="loading">
      <Spinner show inline />
    </div>
  {:else}
    <SectionItem
      label={$format('label.SELECT_USERS')}
      description={isEnableAction
        ? $format('label.CHOOSE_USERS_TO_ENABLE')
        : isDisableAction
          ? $format('label.CHOOSE_USERS_TO_DISABLE')
          : $format('label.CHOOSE_USERS_REMINDER')}
    >
      <Select
        id="user-actions-select"
        multiple
        searchable
        clearable
        placeholder={$format('label.SELECT_USERS')}
        bind:items={users}
        bind:value={selectedUsers}
        bind:filterText
        disabled={loading}
      >
        <svelte:fragment slot="item" let:item>
          <div class="item">
            {#key item}
              <Porthole details={item} />
            {/key}
            <div class="item-detail">
              <div class="name">{item.name}</div>
              <div class="email">{item.email}</div>
            </div>
          </div>
        </svelte:fragment>
        <div slot="empty" class="empty">
          {$format('label.NO_OPTIONS_AVAILABLE')}
        </div>
      </Select>
    </SectionItem>
  {/if}
</div>

<style lang="scss">
  .user-actions-form {
    .loading {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: var(--padding-large);
    }

    :global(.section-item .row.align-items-center) {
      align-items: flex-start !important;
    }

    .selected-count {
      margin-top: var(--margin-small);
      font-size: var(--font-size-small);
      color: var(--color-gray-700);
      font-weight: var(--font-weight-medium);
    }
  }

  .item {
    align-items: center;
    display: grid;
    grid-template-columns: 48px auto;
    gap: 16px;

    .item-detail {
      .name {
        font-weight: var(--font-weight-medium);
      }

      .email {
        color: var(--color-gray-600);
        font-size: var(--font-size-medium);
      }
    }

    &.active {
      .item-detail {
        .email {
          color: var(--color-blue-100);
        }
      }
    }
  }

  .empty {
    color: var(--color-gray-600);
    padding: var(--padding-xlarge);
    text-align: center;
  }
</style>
